import React, {Fragment, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Redirect} from 'react-router-dom';
import Dialog from 'react-bootstrap-dialog';
import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'moment/locale/en-gb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Badge, Button, Fade, Form, Modal, Row, Spinner} from 'react-bootstrap';
import {
  FaBan,
  FaBell,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCreditCard,
  FaEuroSign,
  FaLock,
  FaMapMarkerAlt,
  FaPlayCircle,
  FaQuestionCircle,
  FaSave,
  FaWallet,
  FaUserFriends,
  FaUsers
} from 'react-icons/fa';
import {calendarMessages} from '../constants';
import PlayerBooking from './player-booking';
import SkyworkPastSlotModal from './skywork-past-slot-modal';
import {useTranslation} from 'react-i18next';
import {getDiscountedBookingPrice} from '../util';
import BookingGuideModal from './booking-guide-modal';
import {createOpenMatch, getPlayerSportRankings, joinOpenMatch, savePlayerSportRankingQuestionnaire} from '../api';
import {mergeRankingQuestionnaires, normalizePlayerRankings, playerRankingQuestionnaires, rankingOptionLabel, rankingQuestionLabel, upsertPlayerRanking} from '../constants/player-ranking-questionnaires';
import {CLUB_PENDING_BOOKING_SLOT_KEY} from '../util/club-mode';
import {getBookingTopupResume, suggestedBookingTopupAmount} from '../util/booking-topup-resume';
import {
  bookingSlotAccessibleLabel,
  compactCapacityLabel,
  isFullAvailableSlot,
  isMultiPlaceSlot,
  isOpenMultiPlaceSlot,
  playerBookingSlotPresentation,
  playerBookingSlotColor,
  playerBookingSlotTitle,
  PLAYER_OWN_BOOKING_COLOR
} from '../util/booking-slot-capacity';

const BOOKING_SLOT_LEGEND = [
  {color: '#31BC8C', labelKey: 'BOOKING_SLOT_SINGLE', descriptionKey: 'BOOKING_SLOT_SINGLE_DESCRIPTION'},
  {color: '#25C75B', labelKey: 'BOOKING_SLOT_PLACES', descriptionKey: 'BOOKING_SLOT_PLACES_DESCRIPTION'},
  {color: '#308771', labelKey: 'BOOKING_SLOT_INVITATION', descriptionKey: 'BOOKING_SLOT_INVITATION_DESCRIPTION'},
  {color: '#3598DB', labelKey: 'BOOKED', descriptionKey: 'BOOKING_SLOT_BOOKED_DESCRIPTION'},
  {color: PLAYER_OWN_BOOKING_COLOR, labelKey: 'BOOKING_SLOT_YOURS', descriptionKey: 'BOOKING_SLOT_YOURS_DESCRIPTION'},
  {color: '#ADB5BD', labelKey: 'BOOKING_SLOT_RESERVED', descriptionKey: 'BOOKING_SLOT_RESERVED_DESCRIPTION'},
  {color: '#444444', labelKey: 'BOOKING_SLOT_UNAVAILABLE', descriptionKey: 'BOOKING_SLOT_UNAVAILABLE_DESCRIPTION'}
];

const openMatchSportLabels = {
  tennis: 'Tennis',
  padel: 'Padel',
  pickleball: 'Pickleball'
};
const normalizeSport = sport => String(sport || '')
  .toLowerCase()
  .trim()
  .replace(/^sport[-_\s]*/, '')
  .replace(/^replayer[-_\s]*/, '');

const sameResourceId = (left, right) => (
  left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right)
);

export const playerFieldAffiliationDiscount = ({affiliations = [], partnerId, fieldId, fieldSport}) => affiliations.reduce((result, membership) => {
  const currentAffiliation = membership && membership.affiliation;
  if (
    !currentAffiliation ||
    membership.valid !== true ||
    currentAffiliation.active !== true ||
    !sameResourceId(currentAffiliation.partnerId, partnerId) ||
    !(currentAffiliation.fields || []).some(currentField => sameResourceId(currentField.id, fieldId))
  ) return result;

  const sportPricing = (currentAffiliation.sportPricing || []).find(pricing => (
    normalizeSport(pricing.sport) === normalizeSport(fieldSport)
  ));
  const currentDiscount = currentAffiliation.pricingMode === 'dedicated_field_cost'
    ? Number(sportPricing ? sportPricing.normalDiscount : 0)
    : Number(currentAffiliation.discount || 0);
  return currentDiscount > result ? currentDiscount : result;
}, 0);
const openMatchSportDisplayName = sport => openMatchSportLabels[normalizeSport(sport)] || String(sport || '');

const openMatchCreditMessage = (credit, t) => {
  if (!credit) return t('OPEN_MATCH_CREDIT_INSUFFICIENT_JOIN');
  return t('OPEN_MATCH_CREDIT_INSUFFICIENT_DETAIL', {
    wallet: credit.walletEur.toFixed(2),
    held: credit.heldEur.toFixed(2),
    required: credit.requiredEur.toFixed(2)
  });
};

const openMatchSlotLabel = (openMatch, t) => {
  if (!openMatch) return null;
  if (openMatch.status === 'confirmed') return t('OPEN_MATCH_STATUS_CONFIRMED_BOOKED');
  if (openMatch.status === 'full') return t('OPEN_MATCH_STATUS_FULL');
  return t('OPEN_MATCH_STATUS_OPEN');
};

export const useSyncedPlannerDate = initialPlannerDate => {
  const initialPlannerTimestamp = initialPlannerDate instanceof Date && !Number.isNaN(initialPlannerDate.getTime())
    ? initialPlannerDate.getTime()
    : null;
  const [plannerDate, setPlannerDate] = useState(() => (
    initialPlannerTimestamp === null ? new Date() : new Date(initialPlannerTimestamp)
  ));

  React.useEffect(() => {
    if (initialPlannerTimestamp === null) return;
    setPlannerDate(currentDate => (
      currentDate.getTime() === initialPlannerTimestamp
        ? currentDate
        : new Date(initialPlannerTimestamp)
    ));
  }, [initialPlannerTimestamp]);

  return [plannerDate, setPlannerDate];
};

export const controlledPlannerCalendarProps = (plannerDate, setPlannerDate) => ({
  date: plannerDate,
  onNavigate: setPlannerDate
});

const sportLabelValue = sport => {
  if (!sport) return '';
  if (typeof sport === 'string' || typeof sport === 'number') return String(sport);
  if (typeof sport === 'object') return sport.name || sport.label || sport.value || sport.sport || sport.type || '';
  return String(sport);
};

const PlayerBookingCalendar = ({
  addToBookingQueue,
  isLogged,
  selectedPartner,
  selectedPartnerFields,
  slots,
  isBookingSlotModalVisible,
  bookingSlot,
  hideBookingSlotModal,
  bookingSlotSelected,
  saveBooking,
  bookingFieldId,
  bookings,
  booking,
  requestDelete,
  purchaseVideo,
  showMessage,
  bookingSlots,
  bookingMultiSlots,
  profile,
  token,
  acceptPrivacy,
  getSlotsByWeek,
  getSlotsByDay,
  showVideoStore,
  showCalendar,
  filterBookableFields,
  affiliations,
  affiliationsStatus,
  refreshPlayerAffiliations,
  deleteBooking,
  enforceWalletBeforeBooking,
  goToProfile,
  startBookingTopUp,
  startGuestTopUp,
  resumeSlotId,
  onResumeSlotHandled,
  initialPlannerDate,
  restoreBookingTopupContext,
}) => {
  const {t, i18n} = useTranslation();
  const [view, setCurrentView] = useState(bookingFieldId ? Views.WEEK : Views.DAY);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileFieldId, setMobileFieldId] = useState(bookingFieldId || '');
  const [plannerDate, setPlannerDate] = useSyncedPlannerDate(initialPlannerDate);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mobileCurrentTimeTop, setMobileCurrentTimeTop] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailBookingActive, setDetailBookingActive] = useState(false);
  const [showPastSlotModal, setShowPastSlotModal] = useState(false);
  const [insufficientCredit, setInsufficientCredit] = useState(null);
  const [showBookingGuide, setShowBookingGuide] = useState(false);
  const [showOpenMatchModal, setShowOpenMatchModal] = useState(false);
  const [openMatchDraft, setOpenMatchDraft] = useState({matchType: 'friendly', playersRequired: 4, levelRangeMode: 'similar', minLevel: '', maxLevel: '', title: '', notes: ''});
  const [openMatchLoading, setOpenMatchLoading] = useState(false);
  const [openMatchRankingSlot, setOpenMatchRankingSlot] = useState(null);
  const [openMatchRankingSport, setOpenMatchRankingSport] = useState('tennis');
  const [openMatchRankings, setOpenMatchRankings] = useState([]);
  const [openMatchQuestionnaires, setOpenMatchQuestionnaires] = useState(playerRankingQuestionnaires);
  const [openMatchRankingAnswers, setOpenMatchRankingAnswers] = useState({});
  const [openMatchRankingSaving, setOpenMatchRankingSaving] = useState(false);

  React.useEffect(() => {
    if (isLogged && token && affiliationsStatus === 'idle') refreshPlayerAffiliations();
  }, [affiliationsStatus, isLogged, refreshPlayerAffiliations, token]);
  const [recoveryHydrating, setRecoveryHydrating] = useState(false);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const [plannerLoading, setPlannerLoading] = useState(Boolean(selectedPartner));
  const processedResumeSlotId = useRef(null);
  const plannerRequestId = useRef(0);
  const mobileSlotRefs = useRef({});
  const resumeSlotHandlers = useRef({events: [], checkPrivacy: null, handleSlotSelection: null});

  React.useEffect(() => {
    if (!isLogged || selectedPartner || !resumeSlotId || recoveryAttempted) return;
    const resume = getBookingTopupResume();
    setRecoveryAttempted(true);
    if (!resume) return;
    setRecoveryHydrating(true);
    Promise.resolve(restoreBookingTopupContext())
      .finally(() => setRecoveryHydrating(false));
  }, [isLogged, recoveryAttempted, restoreBookingTopupContext, resumeSlotId, selectedPartner]);

  const loadPlannerSlots = React.useCallback(async (targetView, targetDate) => {
    if (!selectedPartner) return;
    const requestId = plannerRequestId.current + 1;
    plannerRequestId.current = requestId;
    setPlannerLoading(true);
    try {
      if (targetView === Views.WEEK) {
        await getSlotsByWeek(selectedPartner.uid, bookingFieldId)(targetDate);
      } else {
        await getSlotsByDay(selectedPartner.uid, bookingFieldId)(targetDate);
      }
    } finally {
      if (plannerRequestId.current === requestId) setPlannerLoading(false);
    }
  }, [bookingFieldId, getSlotsByDay, getSlotsByWeek, selectedPartner]);

  React.useEffect(() => {
    loadPlannerSlots(view, plannerDate);
  }, [loadPlannerSlots, plannerDate, view]);

  const loadOpenMatchRankingSetup = React.useCallback(async () => {
      const res = await getPlayerSportRankings(token);
      if (res.status === 200) {
        const normalizedRankings = normalizePlayerRankings(res.json.rankings);
        setOpenMatchRankings(normalizedRankings);
        setOpenMatchQuestionnaires(mergeRankingQuestionnaires(res.json.questionnaires));
        return {...res.json, rankings: normalizedRankings};
      }
      setOpenMatchRankings(normalizePlayerRankings([]));
      setOpenMatchQuestionnaires(playerRankingQuestionnaires);
      showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_RANKING_LOAD_ERROR', {
        error: res.json && res.json.error ? res.json.error : res.status
      }));
      return null;
  }, [showMessage, t, token]);

  React.useEffect(() => {
    if (token) loadOpenMatchRankingSetup();
  }, [loadOpenMatchRankingSetup, token]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateMobile = () => setIsMobile(mediaQuery.matches);
    updateMobile();
    mediaQuery.addListener(updateMobile);
    return () => mediaQuery.removeListener(updateMobile);
  }, []);

  React.useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  let dialog = null;
  const confirmPurchase = slot => {
    dialog.show({
      title: t('VIDEO_STORE'),
      body: t('ENTER_TO_VIDEOSTORE_TO_PURCHASE'),
      actions: [
        Dialog.Action(
          t('GO_TO_VIDEO_STORE'),
          () => showVideoStore(selectedPartner.uid, slot.fieldId, slot.startDatetime, slot.endDatetime),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('NO'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const checkPrivacyDialog = actionAfterCheck => {
    dialog.show({
      title: t('PRIVACY_CONSENT'),
      body: <Fragment>
              <Form.Label>
                <strong>{t('VIDEO_RECORDING_ACTIVE')}</strong>
              </Form.Label>
              <Form.Label>
                {t('GIVE_CONSENT_NOW')}
              </Form.Label>
            </Fragment>,
      actions: [
        Dialog.Action(
          t('I_AGREE'),
          () => {
            acceptPrivacy();
            dialog.show({
              title: t('PRIVACY_CONSENT'), 
              body: t('ABLE_TO_PURCHASE_YOUR_MATCH'),
              actions: [Dialog.Action('Ok', actionAfterCheck,'btn btn-secondary')]
            });
          },
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('NO'),
          null,          
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const insufficientWalletDialog = (price, event) => {
    const wallet = Number(profile.user.wallet || 0);
    setInsufficientCredit({
      event,
      requiredCredit: Number(price || 0),
      wallet,
      topUpAmount: suggestedBookingTopupAmount(price, wallet)
    });
  };

  const missingTaxDataDialog = () => {
    dialog.show({
      title: t('TAX_DATA_MISSING'),
      body: <Fragment>
              <Form.Label>
                {t('TAX_DATA_MISSING_INFO')}
              </Form.Label>
            </Fragment>,
      actions: [
        Dialog.Action(
          t('GO_TO_PROFILE'),
          goToProfile,
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('CLOSE_BUTTON'),
          null,          
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const bookingQueue = slotId => {
    dialog.show({
      title: t('BOOKINGS'),
      body: t('BOOKING_QUEUE'),
      actions: [
        Dialog.Action(
          t('NOTIFY_ME'),
          () => addToBookingQueue(slotId),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('CLOSE_BUTTON'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const deleteBookingDialog = (bookingId, multiBooking) => {
    dialog.show({
      title: t('BOOKING_DELETE'),
      body: booking.groupBooking ? t('BOOKING_DELETE_CONFIRM_GROUP') : t('BOOKING_DELETE_CONFIRM'),
      actions: [
        Dialog.Action(
          t('CONFIRM_DELETE'),
          () => deleteBooking(multiBooking ? booking.users.filter(u => u.uid === profile.uid)[0].id : bookingId, selectedPartner.uid, multiBooking),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('DO_NOT_DELETE'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const affiliation = affiliations.filter(a => selectedPartner && a.affiliation && sameResourceId(a.affiliation.partnerId, selectedPartner.uid) && a.valid === true && a.affiliation.active === true);

  const discount = fieldId => {
    const selectedField = field(fieldId);
    return playerFieldAffiliationDiscount({
      affiliations,
      partnerId: selectedPartner && selectedPartner.uid,
      fieldId,
      fieldSport: selectedField && selectedField.sport
    });
  };

  const discountedSlotPrice = slot => getDiscountedBookingPrice(slot.price, discount(slot.fieldId));

  const dedicatedAffiliations = fieldId => affiliation.filter(item => (
    item.affiliation.pricingMode === 'dedicated_field_cost' &&
    item.affiliation.fields.some(currentField => sameResourceId(currentField.id, fieldId)) &&
    (item.affiliation.sportPricing || []).some(pricing => normalizeSport(pricing.sport) === normalizeSport(field(fieldId).sport))
  ));

  const localizer = momentLocalizer(moment);

  const getEventDescription = slot => {
    if (slot.state === 'reserved') {

      if (slot.videoBought) {
        if (slot.videoReady) {
          return `${slot.description} ${t('VIDEO_PURCHASED_AVAILABLE')}`;
        } else {
          return `${slot.description} ${t('PURCHASED_VIDEO_IN_PROCESSING')}`;
        }
      }

      if (slot.videoSellPermission && slot.videoPurchasable && (!slot.selectedPlayers || slot.selectedPlayers.length === 0 || slot.selectedPlayers.some(player => player.uid === profile.uid))) {
        return `${slot.description} Video acquistabile al prezzo di ${slot.videoPrice} Euro`;
      }

      return slot.description;
    }

    if (slot.state === 'unavailable') {
      return slot.description;
    }

    const myBooking = bookings.find(b => b.bookingId === slot.bookingId);

    if (isMultiPlaceSlot(slot)) {
      if (isFullAvailableSlot(slot)) return t('BOOKED');
      return myBooking ? t('BOOKING_SLOT_YOURS') : t('BOOKING_SLOT_AVAILABLE');
    }
  
    if (slot.bookingId) {
      if (myBooking) {
        return myBooking.description;
      }

      return t('BOOKED');
    }

    if (slot.description) {
      return slot.description;
    }

    return t('AVAILABLE');
  };

  const getBooking = bookingId => {
    return bookings.find(b => b.bookingId === bookingId);
  };

  const events = slots.map(slot => {
    return {
      id: slot.slotId,
      title: getEventDescription(slot),
      start: new Date(slot.startDatetime),
      end: new Date(slot.endDatetime),
      slot,
      resourceId: slot.fieldId,
      bookingId: slot.bookingId,
      state: slot.state,
      booking: slot.bookingId ? getBooking(slot.bookingId) : null
    };
  });

  const plannerFieldOptions = selectedPartnerFields.filter(f => f.bookable === filterBookableFields);
  const availableFields = bookingFieldId ? selectedPartnerFields.filter(f => f.id === bookingFieldId) : plannerFieldOptions;
  const selectedMobileField = availableFields.find(f => String(f.id) === String(mobileFieldId)) || availableFields[0];
  const filteredFields = isMobile && selectedMobileField
    ? [selectedMobileField]
    : availableFields;

  React.useEffect(() => {
    if (isMobile && availableFields.length > 0 && !availableFields.some(f => String(f.id) === String(mobileFieldId))) {
      setMobileFieldId(availableFields[0].id);
    }
  }, [availableFields, isMobile, mobileFieldId]);

  const showAllFields = () => {
    setCurrentView(Views.DAY);
    setMobileFieldId('');
    showCalendar(selectedPartner.uid, null)();
  };

  const selectPlannerField = fieldId => {
    if (!fieldId) {
      showAllFields();
      return;
    }

    setCurrentView(Views.DAY);
    setMobileFieldId(fieldId);
    showCalendar(selectedPartner.uid, Number(fieldId))();
  };

  const resourceMap = filteredFields.map(f => {
    return {
      resourceId: f.id,
      resourceTitle: (
        <button
          type="button"
          className="skywork-planner-field-button"
          onClick={() => {
            setCurrentView(Views.WEEK);
            showCalendar(selectedPartner.uid, f.id)();
          }}
        >
          <FaMapMarkerAlt aria-hidden="true"/>
          <span className="skywork-planner-field-heading">
            <strong>{f.name}</strong>
            {(f.sport || f.terrain) && <small>{[sportLabelValue(f.sport), f.terrain].filter(Boolean).join(' · ')}</small>}
          </span>
        </button>
      )
    };
  });

  const field = React.useCallback(
    fieldId => selectedPartnerFields.find(f => String(f.id) === String(fieldId)),
    [selectedPartnerFields]
  );

  const minTime = new Date();
  minTime.setHours(7, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(23, 58, 59);

  const eventStyleGetter = event => {
    let backgroundColor = event.slot.state === 'reserved' ? event.slot.color ? event.slot.color : '#ADB5BD' : event.slot.state === "unavailable" ? '#444444' : isFullAvailableSlot(event.slot) ? '#3598DB' : '#31BC8C';

    //individuale
    if (event.slot.state === 'available' && event.slot.availablePlaces > 0) {
      backgroundColor = '#31BC8C';
    }
    //multipla singola
    if (event.slot.state === 'available' && event.slot.availablePlaces > 0 && event.slot.presetPlaces > 1 && event.slot.minimumPlayers === null) {
      backgroundColor = '#25C75B';
    }
    //multipla di gruppo
    if (event.slot.state === 'available' && event.slot.availablePlaces > 0 && event.slot.presetPlaces > 1 && event.slot.minimumPlayers !== null) {
      backgroundColor = '#308771';
    }

    backgroundColor = playerBookingSlotColor(event, backgroundColor);

    const color = '#fff';
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 1,
      color,
      border: '1px',
      borderStyle: 'solid',
      borderColor: '#00152F',
      display: 'block'
    };
    return {
      className: `booking-calendar-slot booking-calendar-slot--${playerBookingSlotPresentation(event)}`,
      style
    };
  };

  const bookingSlotStatus = event => {
    const presentation = playerBookingSlotPresentation(event);
    if (presentation === 'reserved') return t('BOOKING_SLOT_RESERVED');
    if (presentation === 'unavailable') return t('BOOKING_SLOT_UNAVAILABLE');
    if (presentation === 'yours') return t('BOOKING_SLOT_YOURS');
    if (isOpenMultiPlaceSlot(event.slot)) return compactCapacityLabel(event.slot, t);
    if (presentation === 'booked') return t('BOOKED');
    if (presentation === 'invitation') return t('BOOKING_SLOT_INVITATION');
    if (presentation === 'places') return t('BOOKING_SLOT_PLACES');
    return t('BOOKING_SLOT_SINGLE');
  };

  const bookingSlotClass = event => playerBookingSlotPresentation(event);

  const mobileSlotColor = event => {
    if (event.slot.state === 'reserved') return event.slot.color || '#ADB5BD';
    if (event.slot.state === 'unavailable') return '#444444';
    if (isFullAvailableSlot(event.slot)) return playerBookingSlotColor(event, '#3598DB');
    if (event.slot.minimumPlayers !== null) return playerBookingSlotColor(event, '#308771');
    if (event.slot.presetPlaces > 1) return playerBookingSlotColor(event, '#25C75B');
    return playerBookingSlotColor(event, '#31BC8C');
  };

  const BookingSlotIcon = ({event}) => {
    const presentation = playerBookingSlotPresentation(event);
    if (presentation === 'yours') return <FaCheckCircle/>;
    if (event.slot.openMatch && event.slot.openMatch.status === 'open') return <FaUsers/>;
    if (presentation === 'reserved') return event.slot.videoPurchasable ? <FaPlayCircle/> : <FaLock/>;
    if (presentation === 'unavailable') return <FaBan/>;
    if (presentation === 'booked') return <FaBell/>;
    if (presentation === 'invitation') return <FaUserFriends/>;
    if (presentation === 'places') return <FaUsers/>;
    return <FaCalendarAlt/>;
  };

  const BookingCalendarEvent = ({event}) => {
    const status = bookingSlotStatus(event);
    const displayTitle = playerBookingSlotTitle(event, [
      t('BOOKED'),
      t('BOOKING_SLOT_YOURS'),
      t('BOOKING_SLOT_AVAILABLE')
    ]);
    const currentField = field(event.slot.fieldId);
    const accessibleLabel = bookingSlotAccessibleLabel({
      status,
      fieldName: currentField && currentField.name,
      startTime: moment(event.start).format('HH:mm'),
      endTime: moment(event.end).format('HH:mm')
    });

    return (
      <div className="booking-calendar-slot-content" aria-label={accessibleLabel}>
        <span className="booking-calendar-slot-status"><BookingSlotIcon event={event}/>{status}</span>
        {event.slot.openMatch ? <span className="booking-calendar-open-match-chip">{openMatchSlotLabel(event.slot.openMatch, t)}</span> : null}
        {displayTitle ? <span className="booking-calendar-slot-title">{displayTitle}</span> : null}
      </div>
    );
  };

  const PlannerToolbar = ({onNavigate, onView, label}) => (
    <div className="skywork-planner-toolbar">
      <Button className="skywork-planner-today" variant="outline-light" onClick={() => onNavigate('TODAY')}>
        {t('BOOKING_CALENDAR_TODAY')}
      </Button>
      <div className="skywork-planner-toolbar-navigation">
        <Button variant="outline-light" onClick={() => onNavigate('PREV')} aria-label={t('BOOKING_CALENDAR_PREVIOUS')}><FaChevronLeft/></Button>
        <div className="skywork-planner-toolbar-date">
          <FaCalendarAlt aria-hidden="true"/>
          <span>{label}</span>
        </div>
        <Button variant="outline-light" onClick={() => onNavigate('NEXT')} aria-label={t('BOOKING_CALENDAR_NEXT')}><FaChevronRight/></Button>
      </div>
      <div className="skywork-planner-toolbar-view" role="group" aria-label={t('BOOKING_CALENDAR_VIEW')}>
        <Button className={view === Views.DAY ? 'active' : ''} variant="outline-light" onClick={() => onView(Views.DAY)}>{t('BOOKING_CALENDAR_DAY')}</Button>
        <Button className={view === Views.WEEK ? 'active' : ''} variant="outline-light" disabled={!bookingFieldId} onClick={() => onView(Views.WEEK)}>{t('BOOKING_CALENDAR_WEEK')}</Button>
      </div>
      <Form.Control
        as="select"
        className="skywork-planner-toolbar-fields"
        aria-label={t('BOOKING_CALENDAR_CHOOSE_FIELD')}
        value={bookingFieldId || ''}
        onChange={event => selectPlannerField(event.target.value)}
      >
        <option value="">{t('BOOKING_CALENDAR_ALL_FIELDS_OPTION')}</option>
        {plannerFieldOptions.map(currentField => <option key={currentField.id} value={currentField.id}>{currentField.name}</option>)}
      </Form.Control>
    </div>
  );

  const checkPrivacy = (actionAfterCheck, event) => {
    const selectedEventField = event.slot ? field(event.slot.fieldId) : null;
    const slotCosts = event.slot && event.slot.costsPrice ? event.slot.costsPrice / 100 : 0;
    const requiredCredit = event.slot && event.slot.minimumPlayers != null
      ? discountedSlotPrice(event.slot) + (slotCosts / event.slot.minimumPlayers)
      : event.slot
        ? discountedSlotPrice(event.slot) + slotCosts
        : 0;
    const requiresOnlinePayment = event.slot && field(event.slot.fieldId) && field(event.slot.fieldId).payment;

    if(selectedEventField && selectedEventField.recording && !profile.privacy1 && event.slot && event.slot.minimumPlayers === null && (profile.country === null || profile.city === null || profile.province === null || profile.cap === null)) {
      missingTaxDataDialog();
    }
    else if (selectedEventField && selectedEventField.recording && !profile.privacy1 && event.slot && event.slot.minimumPlayers === null) {
      checkPrivacyDialog(actionAfterCheck);
    } 
    else if (
      event.slot &&
      !event.bookingId &&
      (event.slot.minimumPlayers != null || (enforceWalletBeforeBooking && requiresOnlinePayment)) &&
      profile.user.wallet < requiredCredit
    ) {
      insufficientWalletDialog(requiredCredit, event);
    } else {
      actionAfterCheck();
    }
  }

  const handleSlotSelection = event => () => {
    let myBooking = null;
    const selectedEventField = field(event.slot.fieldId);
    if (event.slot.bookingId) {
      myBooking = bookings.find(b => b.bookingId === event.slot.bookingId);
    }
    if (selectedEventField && selectedEventField.recording && ((event.slot.state === "reserved" && (!event.slot.selectedPlayers || event.slot.selectedPlayers.length === 0 || event.slot.selectedPlayers.some(player => player.uid === profile.uid))) || (event.slot.state === "available" && myBooking && new Date(event.slot.endDatetime) < Date.now())) && event.slot.videoPurchasable && event.slot.videoSellPermission) {
      confirmPurchase(event.slot);
    } else if (event.slot.bookingId && !myBooking && event.slot.availablePlaces < 1 && new Date(event.slot.startDatetime) > Date.now()) {
      bookingQueue(event.slot.slotId);
    } else {
      bookingSlotSelected(event);
    }
  };

  resumeSlotHandlers.current = {events, checkPrivacy, handleSlotSelection};

  React.useEffect(() => {
    if (!isLogged || !slots.length) return;

    const sessionResumeSlotId = typeof window !== 'undefined'
      ? window.sessionStorage.getItem(CLUB_PENDING_BOOKING_SLOT_KEY)
      : null;
    const effectiveResumeSlotId = resumeSlotId || sessionResumeSlotId;
    if (!effectiveResumeSlotId) return;
    if (processedResumeSlotId.current === String(effectiveResumeSlotId)) return;

    const {events: resumeEvents} = resumeSlotHandlers.current;
    const event = resumeEvents.find(candidate => String(candidate.slot.slotId) === String(effectiveResumeSlotId));
    if (!event) return;
    if (!field(event.slot.fieldId) || filteredFields.length === 0) return;

    processedResumeSlotId.current = String(effectiveResumeSlotId);
    if (sessionResumeSlotId && typeof window !== 'undefined') {
      window.sessionStorage.removeItem(CLUB_PENDING_BOOKING_SLOT_KEY);
    }
    if (resumeSlotId && onResumeSlotHandled) onResumeSlotHandled();
    if (new Date(event.slot.startDatetime).getTime() <= Date.now()) {
      setShowPastSlotModal(true);
      return;
    }

    setDetailBookingActive(false);
    setDetailEvent(event);

    if (
      event.slot.state === 'available' &&
      (!event.slot.bookingId || event.booking || Number(event.slot.availablePlaces) > 0)
    ) {
      checkPrivacy(() => {
        setDetailBookingActive(true);
        handleSlotSelection(event)();
      }, event);
      return;
    }

    if (event.slot.state !== 'available' || Number(event.slot.availablePlaces) < 1) {
      showMessage('BOOKINGS', 'EVENT_ALREADY_BOOKED');
    }
  }, [checkPrivacy, field, filteredFields.length, handleSlotSelection, isLogged, onResumeSlotHandled, resumeSlotId, selectedPartnerFields.length, showMessage, slots.length]);

  const handleDeleteBooking = (bookingSlot, phoneNumber) => () => {
    if(field(bookingSlot.fieldId).unbookableHours === null)
      requestDelete(phoneNumber);
    else{
      const deleteDeadline = new Date(bookingSlot.startDatetime);
      deleteDeadline.setHours(deleteDeadline.getHours() - field(bookingSlot.fieldId).unbookableHours);
      if(Date.now() > deleteDeadline) showMessage('BOOKING',t("BOOKING_DELETE_REJECTED",{hours: field(bookingSlot.fieldId).unbookableHours}));
      else deleteBookingDialog(bookingSlot.bookingId, bookingSlot.presetPlaces !== 1 && bookingSlot.minimumPlayers === null);
    }
  };
  
  const handleSlotMultiselection = event => {
    // seleziono slot nel range
    const selectedSlots = slots.filter(s => s.fieldId === event.resourceId && new Date(s.startDatetime) < event.end && new Date(s.endDatetime) > event.start);
    // la selezione non contiene elementi
    if (selectedSlots.length === 0) {      
      showMessage('BOOKING', 'BOOKING_EMPTY_SELECTION');
      return;
    }
    // Controllo che non ci siano eventi non disponibili
    const unavailableSlots = selectedSlots.filter(s => s.state === 'unavailable');
    if (unavailableSlots.length !== 0) {
      showMessage('BOOKING', 'BOOKING_EVENT_UNAVAILABLE');
      return;
    }
    // controllo che non ci siano eventi riservati
    const reservedSlots = selectedSlots.filter(s => s.state === 'reserved');
    if (reservedSlots.length !== 0) {      
      showMessage('BOOKING', 'BOOKING_EVENT_RESERVED');
      return;
    }
    // Controllo che non ci siamo eventi con la prenotazione multipla
    for (let i = 0; i < selectedSlots.length; i++) {
      if (selectedSlots[i].presetPlaces > 1) {
        showMessage('BOOKINGS', 'BOOKING_EVENT_NOT_ALLOWED');
        return;
      }
    }
    // controllo che non ci siano eventi prenotati
    const bookingSlots = selectedSlots.filter(s => s.bookingId);
    if (selectedSlots.length === 1 && bookingSlots.length === 1) {
      const myBooking = bookings.find(b => b.bookingId === bookingSlots[0].bookingId);
      if (!myBooking) {
        bookingQueue(bookingSlots[0].slotId);
        return;
      }
    } else if (selectedSlots.length > 1 && bookingSlots.length !== 0) {
      showMessage('BOOKING', 'EVENT_ALREADY_BOOKED');
      return;
    }

    // ordino per startDate
    selectedSlots.sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime));
    // controllo che siano adiacenti (startDate === endDate precedente)
    let endDateTarget = null;
    for (let i = 0; i < selectedSlots.length; i++) {
      if (endDateTarget !== null && endDateTarget !== selectedSlots[i].startDatetime) {
        showMessage('BOOKING', 'BOOKING_SELECTION_NOT_ADJACENT');
        return;
      }

      endDateTarget = selectedSlots[i].endDatetime;
    }
    
    // Ultimo controllo!! La data di inizio del primo evento non deve essere nel passato
    if (new Date(selectedSlots[0].startDatetime) < new Date()) {
      setShowPastSlotModal(true);
      return;
    }

    // La selezione è valida, posso prenotare!!
    bookingMultiSlots(selectedSlots);
  };

  const openSlotRange = event => {
    if (new Date(event.start).getTime() <= Date.now()) {
      setShowPastSlotModal(true);
      return;
    }
    checkPrivacy(() => handleSlotMultiselection(event), event);
  };

  const navigateMobileDay = dayOffset => {
    const nextDate = dayOffset === 0 ? new Date() : moment(plannerDate).add(dayOffset, 'day').toDate();
    setPlannerDate(nextDate);
    loadPlannerSlots(Views.DAY, nextDate);
  };

  const mobileEvents = events
    .filter(event => selectedMobileField && String(event.resourceId) === String(selectedMobileField.id))
    .filter(event => moment(event.start).isSame(plannerDate, 'day'))
    .sort((first, second) => first.start - second.start);

  React.useLayoutEffect(() => {
    const updateIndicatorPosition = () => {
      if (!isMobile || !moment(plannerDate).isSame(currentTime, 'day') || mobileEvents.length === 0) {
        setMobileCurrentTimeTop(null);
        return;
      }

      const now = currentTime.getTime();
      const firstStart = mobileEvents[0].start.getTime();
      const lastEnd = mobileEvents[mobileEvents.length - 1].end.getTime();
      if (now < firstStart || now > lastEnd) {
        setMobileCurrentTimeTop(null);
        return;
      }

      let top = null;
      for (let index = 0; index < mobileEvents.length; index += 1) {
        const event = mobileEvents[index];
        const element = mobileSlotRefs.current[event.id];
        if (!element) continue;
        const start = event.start.getTime();
        const end = event.end.getTime();

        if (now >= start && now <= end) {
          const ratio = Math.min(1, Math.max(0, (now - start) / Math.max(end - start, 1)));
          top = element.offsetTop + (element.offsetHeight * ratio);
          break;
        }

        const nextEvent = mobileEvents[index + 1];
        const nextElement = nextEvent ? mobileSlotRefs.current[nextEvent.id] : null;
        if (nextEvent && nextElement && now > end && now < nextEvent.start.getTime()) {
          const ratio = (now - end) / Math.max(nextEvent.start.getTime() - end, 1);
          const gapStart = element.offsetTop + element.offsetHeight;
          top = gapStart + ((nextElement.offsetTop - gapStart) * ratio);
          break;
        }
      }
      setMobileCurrentTimeTop(top);
    };

    updateIndicatorPosition();
    window.addEventListener('resize', updateIndicatorPosition);
    return () => window.removeEventListener('resize', updateIndicatorPosition);
  }, [currentTime, isMobile, mobileEvents, plannerDate]);

  const mobileSlotAction = event => {
    if (event.slot.openMatch && event.slot.openMatch.status === 'open' && !event.slot.openMatch.currentUserJoined) return 'Partecipa';
    if (isFullAvailableSlot(event.slot) && !event.booking) return t('NOTIFY_ME');
    if (event.booking) return t('BOOKING_SLOT_OPEN');
    if (event.slot.state === 'available' && event.slot.availablePlaces > 0) return t('BOOK');
    return t('BOOKING_SLOT_DETAILS');
  };

  const mobileSlotTitle = event => {
    const title = isMultiPlaceSlot(event.slot)
      ? (isFullAvailableSlot(event.slot) ? t('BOOKED') : (event.booking ? t('BOOKING_SLOT_YOURS') : t('BOOKING_SLOT_AVAILABLE')))
      : (event.slot.description || (event.booking && event.booking.description) || ((event.slot.state !== 'available' || event.slot.bookingId) ? event.title : null));

    return playerBookingSlotTitle({...event, title}, [
      t('BOOKED'),
      t('BOOKING_SLOT_YOURS'),
      t('BOOKING_SLOT_AVAILABLE')
    ]);
  };

  const mobileSlotAccessibleLabel = event => {
    const currentField = field(event.slot.fieldId);
    return bookingSlotAccessibleLabel({
      status: bookingSlotStatus(event),
      fieldName: currentField && currentField.name,
      startTime: moment(event.start).format('HH:mm'),
      endTime: moment(event.end).format('HH:mm')
    });
  };

  const mobileSlotDuration = event => moment.duration(moment(event.end).diff(moment(event.start))).asMinutes();

  const mobileSlotPrice = event => {
    if (event.slot.price <= 0) return null;
    return `${discountedSlotPrice(event.slot).toFixed(2)} €${event.slot.presetPlaces > 1 ? ` · ${t('SKYWORK_BOOKING_PER_PERSON')}` : ''}`;
  };

  const detailActionAvailable = event => {
    if (!event || event.slot.state === 'unavailable') return false;
    if (event.slot.state === 'reserved') {
      return Boolean(event.slot.videoPurchasable && event.slot.videoSellPermission);
    }
    return true;
  };

  const openSlotDetail = event => {
    if (new Date(event.slot.startDatetime).getTime() <= Date.now()) {
      setShowPastSlotModal(true);
      return;
    }
    setDetailBookingActive(false);
    setDetailEvent(event);

    if (
      event.slot.state === 'available' &&
      (event.booking || event.slot.availablePlaces > 0)
    ) {
      checkPrivacy(() => {
        setDetailBookingActive(true);
        handleSlotSelection(event)();
      }, event);
    }
  };

  const closeSlotDetail = () => {
    if (detailBookingActive) hideBookingSlotModal();
    setDetailBookingActive(false);
    setDetailEvent(null);
  };

  const saveBookingAndCloseDetail = async bookingValues => {
    const saved = await saveBooking(bookingValues);
    if (saved) closeSlotDetail();
    return saved;
  };

  const canCreateOpenMatch = event => {
    if (!event || !event.slot || !event.slot.fieldId) return false;
    if (event.slot.openMatch) return false;
    const sport = normalizeSport(openMatchSportForSlot(event.slot));
    return (
      event.slot.state !== 'reserved' &&
      event.slot.state !== 'unavailable' &&
      new Date(event.slot.startDatetime) > Date.now() &&
      ['tennis', 'padel', 'pickleball'].includes(sport)
    );
  };

  const handleJoinOpenMatchFromSlot = async event => {
    if (!event || !event.slot || !event.slot.openMatch) return;
    const res = await joinOpenMatch(event.slot.openMatch.id, token);
    if (res.status === 200) {
      showMessage(t('OPEN_MATCH_KICKER'), res.json && res.json.openMatch && res.json.openMatch.status === 'confirmed'
        ? t('OPEN_MATCH_JOIN_CONFIRMED')
        : t('OPEN_MATCH_JOINED'));
      await loadPlannerSlots(view, plannerDate);
      closeSlotDetail();
      return;
    }
    const reason = res.json && (res.json.reason || res.json.error);
    if (reason === 'ranking_required') {
      setOpenMatchRankingSlot(event.slot);
      setOpenMatchRankingSport(normalizeSport(openMatchSportForSlot(event.slot)) || 'tennis');
      setOpenMatchRankingAnswers({});
      return;
    }
    if (reason === 'not_enough_available_credit') {
      showMessage(t('OPEN_MATCH_KICKER'), openMatchCreditMessage(res.json.credit, t));
      return;
    }
    if (reason === 'slot_no_longer_available') {
      showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_SLOT_CANCELLED_RELEASED'));
      await loadPlannerSlots(view, plannerDate);
      closeSlotDetail();
      return;
    }
    showMessage(t('OPEN_MATCH_KICKER'), res.json && res.json.error ? res.json.error : t('OPEN_MATCH_JOIN_FAILED_GENERIC'));
  };

  const openOpenMatchModal = async event => {
    const sport = normalizeSport(openMatchSportForSlot(event.slot));
    let ranking = openMatchRankingForSport(sport);
    if (!ranking && token) {
      const setup = await loadOpenMatchRankingSetup();
      ranking = setup && setup.rankings ? setup.rankings.find(item => item.sport === sport) : null;
    }
    if (!ranking || !ranking.configured) {
      setDetailEvent(event);
      setOpenMatchRankingSlot(event.slot);
      setOpenMatchRankingSport(sport || 'tennis');
      setOpenMatchRankingAnswers({});
      return;
    }
    setDetailEvent(event);
    setOpenMatchDraft({matchType: 'friendly', playersRequired: 4, levelRangeMode: 'similar', minLevel: '', maxLevel: '', title: '', notes: ''});
    setShowOpenMatchModal(true);
  };

  const openMatchSportForSlot = slot => {
    const slotField = slot && slot.fieldId ? field(slot.fieldId) : null;
    return String(slotField && slotField.sport ? slotField.sport : '').toLowerCase();
  };

  const openMatchRankingForSport = sport => openMatchRankings.find(ranking => ranking.sport === sport);

  const renderOpenMatchBookingAction = event => {
    if (!canCreateOpenMatch(event)) return null;
    return (
      <Button type="button" className="skywork-open-match-create-button" onClick={() => openOpenMatchModal(event)}>
        <FaUsers aria-hidden="true"/>
        <span>{t('CREATE_MATCH')}</span>
        <small>{t('OPEN_MATCH_SLOT_CREATE_HELP')}</small>
      </Button>
    );
  };

  const openMatchPayloadForSlot = slot => ({
    slotId: slot.slotId,
    matchType: openMatchDraft.matchType,
    playersRequired: Number(openMatchDraft.playersRequired),
    levelRangeMode: openMatchDraft.levelRangeMode || 'similar',
    minLevel: openMatchDraft.levelRangeMode === 'custom' && openMatchDraft.minLevel !== '' ? Number(openMatchDraft.minLevel) : null,
    maxLevel: openMatchDraft.levelRangeMode === 'custom' && openMatchDraft.maxLevel !== '' ? Number(openMatchDraft.maxLevel) : null,
    title: openMatchDraft.title,
    notes: openMatchDraft.notes
  });

  const saveOpenMatch = async () => {
    if (!detailEvent || !detailEvent.slot) return;
    setOpenMatchLoading(true);
    const res = await createOpenMatch(openMatchPayloadForSlot(detailEvent.slot), token);
    if (res.status === 201) {
      setShowOpenMatchModal(false);
      closeSlotDetail();
      window.location.assign(`/matches/${res.json.openMatch.publicId}`);
    } else if (res.json && res.json.error === 'ranking_required_for_sport') {
      setShowOpenMatchModal(false);
      setOpenMatchRankingSlot(detailEvent.slot);
      setOpenMatchRankingAnswers({});
    } else if (res.json && res.json.error === 'invalid_slot') {
      showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_ERROR_SLOT_NOT_AVAILABLE'));
    } else if (res.json && res.json.error === 'not_enough_available_credit') {
      showMessage(t('OPEN_MATCH_KICKER'), openMatchCreditMessage(res.json.credit, t));
    } else {
      showMessage(t('OPEN_MATCH_KICKER'), res.json && res.json.error ? res.json.error : t('OPEN_MATCH_CREATE_ERROR'));
    }
    setOpenMatchLoading(false);
  };

  const saveOpenMatchRankingAndRetry = async () => {
    if (!openMatchRankingSlot) return;
    const sport = openMatchRankingSport;
    const slotSport = normalizeSport(openMatchSportForSlot(openMatchRankingSlot));
    const questionnaire = openMatchQuestionnaires[sport] || {questions: []};
    const allAnswered = questionnaire.questions.every(question => openMatchRankingAnswers[question.key] !== undefined);
    if (!questionnaire.questions.length) {
      showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_QUESTIONNAIRE_UNAVAILABLE'));
      return;
    }
    if (!allAnswered) {
      showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_QUESTIONNAIRE_INCOMPLETE'));
      return;
    }

    setOpenMatchRankingSaving(true);
    const answers = questionnaire.questions.map(question => ({questionKey: question.key, value: openMatchRankingAnswers[question.key]}));
    const rankingRes = await savePlayerSportRankingQuestionnaire(sport, answers, token);
    if (rankingRes.status === 200) {
      const updatedRanking = rankingRes.json && rankingRes.json.ranking ? rankingRes.json.ranking : null;
      if (updatedRanking) {
        setOpenMatchRankings(currentRankings => upsertPlayerRanking(currentRankings, updatedRanking));
      }
      if (sport !== slotSport) {
        setOpenMatchRankingAnswers({});
        setOpenMatchRankingSport(slotSport);
        showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_OTHER_SPORT_RANKING_REQUIRED', {
          savedSport: openMatchSportDisplayName(sport),
          slotSport: openMatchSportDisplayName(slotSport)
        }));
        setOpenMatchRankingSaving(false);
        return;
      }
      setOpenMatchDraft({
        ...openMatchDraft,
        matchType: 'friendly',
        playersRequired: 4,
        levelRangeMode: 'similar',
        minLevel: '',
        maxLevel: '',
        title: '',
        notes: ''
      });
      setDetailEvent({
        slot: openMatchRankingSlot,
        start: new Date(openMatchRankingSlot.startDatetime),
        end: new Date(openMatchRankingSlot.endDatetime),
        title: openMatchRankingSlot.description || field(openMatchRankingSlot.fieldId).name
      });
      setOpenMatchRankingSlot(null);
      setOpenMatchRankingAnswers({});
      setShowOpenMatchModal(true);
      showMessage(t('OPEN_MATCH_KICKER'), t('OPEN_MATCH_RANKING_CONFIGURED'));
    } else {
      showMessage(t('OPEN_MATCH_KICKER'), rankingRes.json && rankingRes.json.error ? rankingRes.json.error : t('OPEN_MATCH_RANKING_SAVE_ERROR'));
    }
    setOpenMatchRankingSaving(false);
  };

  const handleDetailAction = event => {
    const canManageInsideDetail =
      event.slot.state !== 'reserved' &&
      event.slot.state !== 'unavailable' &&
      (event.booking || !event.slot.bookingId || event.slot.availablePlaces > 0);

    if (canManageInsideDetail) {
      checkPrivacy(() => {
        setDetailBookingActive(true);
        handleSlotSelection(event)();
      }, event);
      return;
    }

    closeSlotDetail();
    checkPrivacy(handleSlotSelection(event), event);
  };

  if (!isLogged) {
    return (
      <Redirect to="/login"/>
    );
  }

  if (!selectedPartner || !profile || !profile.user) return (
    <div className="skywork-booking-loading" role="status" aria-live="polite">
      {recoveryHydrating ? <Spinner animation="border" size="sm"/> : null}
      <span>{recoveryHydrating ? t('BOOKING_RECOVERY_LOADING') : t('BOOKING_RECOVERY_UNAVAILABLE')}</span>
    </div>
  );

  if (affiliationsStatus !== 'ready') return (
    <div className="skywork-booking-loading" role="status" aria-live="polite">
      {affiliationsStatus === 'loading' ? <Spinner animation="border" size="sm"/> : null}
      <span>{affiliationsStatus === 'loading' ? t('LOADING_DATA_IN_PROGRESS') : t('BOOKING_RECOVERY_UNAVAILABLE')}</span>
    </div>
  );

  if(selectedPartnerFields.length === 0) return(
    <h4 className='text-center' style={{marginTop: 150, marginBottom: 500}}>{t('PARTNER_NO_FIELDS', {partner:selectedPartner.name})}</h4>
  );

  return (
    <Fragment>
      <Fade in appear className="player-booking-calendar-shell" style={{transition: 'opacity 0.30s linear', marginTop: 100, marginBottom: 100, '--current-time-label': `"${moment(currentTime).format('HH:mm')}"`}}>
        <Row className="justify-content-center player-booking-calendar-layout">
          <div className="skywork-planner-intro">
            <span>{t('BOOKING_CALENDAR_TITLE')}</span>
            <h4>{bookingFieldId ? t('PARTNER_BOOK_FIELD', {partner: selectedPartner.name, field: filteredFields[0].name}) : t('PARTNER_BOOK_FIELD_OR_BUY_VIDEO', {partner: selectedPartner.name})}</h4>
            {bookingFieldId && filteredFields[0].bookingPolicy ? <p>{filteredFields[0].bookingPolicy}</p> : null}
          </div>
          <div className="booking-calendar-guide-action"><Button type="button" variant="outline-primary" onClick={() => setShowBookingGuide(true)}><FaQuestionCircle/> {t('BOOKING_GUIDE')}</Button></div>
          {bookingFieldId ? (
            <div className="skywork-planner-all-fields">
              <Button type="button" variant="outline-primary" onClick={showAllFields}>
                {t('BOOKING_CALENDAR_ALL_FIELDS')}
              </Button>
            </div>
          ) : null}
          <div className="booking-calendar-mobile-field">
            <Form.Label htmlFor="booking-calendar-field-select">{t('BOOKING_CALENDAR_CHOOSE_FIELD')}</Form.Label>
            <small>{t('BOOKING_CALENDAR_CHOOSE_FIELD_HELP')}</small>
            <div className="booking-calendar-mobile-field-control">
              <FaMapMarkerAlt aria-hidden="true"/>
              <Form.Control
                as="select"
                id="booking-calendar-field-select"
                value={selectedMobileField ? selectedMobileField.id : ''}
                onChange={event => setMobileFieldId(event.target.value)}
              >
                {availableFields.map(currentField => (
                  <option key={currentField.id} value={currentField.id}>{currentField.name}</option>
                ))}
              </Form.Control>
            </div>
          </div>
          <div className="booking-calendar-legend-title player-booking-legend-title">{t('BOOKING_CALENDAR_LEGEND')}</div>
          <div className="booking-calendar-legend player-booking-legend">
            {BOOKING_SLOT_LEGEND.map(type => (
              <span key={type.labelKey}>
                <i style={{backgroundColor: type.color}}/>
                <span>
                  <strong>{t(type.labelKey)}</strong>
                  <small>{t(type.descriptionKey)}</small>
                </span>
              </span>
            ))}
          </div>
          {isMobile ? (
            <section className="skywork-mobile-planner" aria-label={t('BOOKINGS')}>
              <header className="skywork-mobile-planner-header">
                <div>
                  <small>{t('BOOKING_CALENDAR_SELECTED_DAY')}</small>
                  <strong>{moment(plannerDate).locale(i18n.language).format('dddd D MMMM')}</strong>
                </div>
                <Button variant="link" onClick={() => navigateMobileDay(0)}>{t('BOOKING_CALENDAR_TODAY')}</Button>
              </header>
              <div className="skywork-mobile-day-navigation">
                <Button variant="outline-light" onClick={() => navigateMobileDay(-1)} aria-label={t('BOOKING_CALENDAR_PREVIOUS')}><FaChevronLeft/></Button>
                <div className="skywork-mobile-day-strip" aria-hidden="true">
                  {[-1, 0, 1].map(offset => {
                    const day = moment(plannerDate).add(offset, 'day');
                    return (
                      <button key={offset} type="button" className={offset === 0 ? 'active' : ''} onClick={() => navigateMobileDay(offset)}>
                        <small>{day.locale(i18n.language).format('ddd')}</small>
                        <strong>{day.format('D')}</strong>
                      </button>
                    );
                  })}
                </div>
                <Button variant="outline-light" onClick={() => navigateMobileDay(1)} aria-label={t('BOOKING_CALENDAR_NEXT')}><FaChevronRight/></Button>
              </div>
              <div className="skywork-mobile-field-heading">
                <FaMapMarkerAlt aria-hidden="true"/>
                <span>
                  <strong>{selectedMobileField ? selectedMobileField.name : ''}</strong>
                  {selectedMobileField && (selectedMobileField.sport || selectedMobileField.terrain) ? (
                    <small>{[selectedMobileField.sport, selectedMobileField.terrain].filter(Boolean).join(' · ')}</small>
                  ) : null}
                </span>
              </div>
              <div className="skywork-mobile-timeline" aria-busy={plannerLoading}>
                {mobileCurrentTimeTop !== null ? (
                  <div className="skywork-mobile-current-time" style={{top: mobileCurrentTimeTop}} aria-label={moment(currentTime).format('HH:mm')}>
                    <span>{moment(currentTime).format('HH:mm')}</span>
                    <i/>
                  </div>
                ) : null}
                {plannerLoading ? (
                  <div className="skywork-mobile-planner-loading" role="status">
                    <Spinner animation="border" size="sm"/>
                    <strong>{t('LOADING_DATA_IN_PROGRESS')}</strong>
                    <small>{moment(plannerDate).locale(i18n.language).format('dddd D MMMM')}</small>
                  </div>
                ) : mobileEvents.length > 0 ? mobileEvents.map(event => (
                  <article
                    key={event.id}
                    ref={element => { mobileSlotRefs.current[event.id] = element; }}
                    className={`skywork-mobile-slot skywork-mobile-slot--${bookingSlotClass(event)} skywork-mobile-slot--assigned-color`}
                    style={{'--mobile-slot-color': mobileSlotColor(event)}}
                  >
                    <div className="skywork-mobile-slot-time">
                      <strong>{moment(event.start).format('HH:mm')}</strong>
                      <span>{moment(event.end).format('HH:mm')}</span>
                    </div>
                    <button
                      type="button"
                      className="skywork-mobile-slot-card"
                      aria-label={mobileSlotAccessibleLabel(event)}
                      onClick={() => openSlotDetail(event)}
                    >
                      <span className="skywork-mobile-slot-status"><BookingSlotIcon event={event}/>{bookingSlotStatus(event)}</span>
                      {mobileSlotTitle(event) ? <strong>{mobileSlotTitle(event)}</strong> : null}
                      <span className="skywork-mobile-slot-facts">
                        <span><FaClock aria-hidden="true"/>{mobileSlotDuration(event)} min</span>
                        {isMultiPlaceSlot(event.slot) ? <span><FaUsers aria-hidden="true"/>{compactCapacityLabel(event.slot, t)}</span> : null}
                      </span>
                      <span className="skywork-mobile-slot-action">{mobileSlotAction(event)} <FaChevronRight aria-hidden="true"/></span>
                    </button>
                  </article>
                )) : (
                  <div className="skywork-mobile-planner-empty">{t('BOOKING_CALENDAR_NO_AVAILABILITY')}</div>
                )}
              </div>
            </section>
          ) : <Calendar
            selectable
            {...controlledPlannerCalendarProps(plannerDate, setPlannerDate)}
            localizer={localizer}
            getNow={() => currentTime}
            style={{'--current-time-label': `"${moment(currentTime).format('HH:mm')}"`}}
            culture={i18n.language ===  'en' ? 'en-GB' : i18n.language}
            defaultView={bookingFieldId ? Views.WEEK : Views.DAY}
            view={view}
            views={bookingFieldId ? ['day', 'week'] : ['day']}
            min={minTime}
            max={maxTime}
            events={events}
            resources={resourceMap}
            step={15}
            timeslots={4}
            resourceIdAccessor="resourceId"
            resourceTitleAccessor="resourceTitle"
            eventPropGetter={eventStyleGetter}
            components={{event: BookingCalendarEvent, toolbar: PlannerToolbar}}
            messages={i18n.language === 'it' ? calendarMessages : null}
            onSelectEvent={openSlotDetail}
            onSelectSlot={openSlotRange}
            onView={setCurrentView}
          />}
        </Row>
      </Fade>
      <Modal
        show={detailEvent !== null}
        onHide={closeSlotDetail}
        dialogClassName={`skywork-slot-detail-dialog ${isMobile ? 'skywork-slot-detail-dialog--mobile' : 'skywork-slot-detail-dialog--desktop'}`}
        className="skywork-slot-detail-modal"
      >
        {detailEvent ? (
          <Fragment>
            <Modal.Header closeButton className={`skywork-slot-detail-header skywork-slot-detail-header--${bookingSlotClass(detailEvent)}`}>
              <div className="skywork-slot-detail-heading">
                <span className="skywork-slot-detail-icon"><BookingSlotIcon event={detailEvent}/></span>
                <div>
                  <small>{bookingSlotStatus(detailEvent)}</small>
                  <Modal.Title>{field(detailEvent.slot.fieldId).name}</Modal.Title>
                  {(field(detailEvent.slot.fieldId).sport || field(detailEvent.slot.fieldId).terrain) ? (
                    <span className="skywork-slot-detail-field-meta">{[field(detailEvent.slot.fieldId).sport, field(detailEvent.slot.fieldId).terrain].filter(Boolean).join(' · ')}</span>
                  ) : null}
                </div>
              </div>
              {renderOpenMatchBookingAction(detailEvent)}
            </Modal.Header>
            <Modal.Body className={`skywork-slot-detail-body ${detailBookingActive ? 'skywork-slot-detail-body--booking' : ''}`}>
              {detailBookingActive ? (
                bookingSlot !== null && booking !== null ? (
                  <PlayerBooking
                    booking={booking}
                    skyworkEmbedded
                    save={saveBookingAndCloseDetail}
                    startGuestTopUp={startGuestTopUp}
                    partner={selectedPartner}
                    slot={bookingSlot}
                    slots={bookingSlots}
                    discount={bookingSlot ? discount(bookingSlot.fieldId) : discount(field(bookingSlots[0].fieldId))}
                    field={bookingSlot ? field(bookingSlot.fieldId) : field(bookingSlots[0].fieldId)}
                    dedicatedAffiliations={dedicatedAffiliations(bookingSlot ? bookingSlot.fieldId : bookingSlots[0].fieldId)}
                    startBookingTopUp={startBookingTopUp}
                  />
                ) : <div className="skywork-slot-detail-loading">{t('LOADING_DATA_IN_PROGRESS')}</div>
              ) : (
              <Fragment>
              <div className="skywork-slot-detail-time">
                <FaClock aria-hidden="true"/>
                <div>
                  <small>{moment(detailEvent.start).locale(i18n.language).format('dddd D MMMM')}</small>
                  <strong>{moment(detailEvent.start).format('HH:mm')}–{moment(detailEvent.end).format('HH:mm')}</strong>
                </div>
              </div>
              {detailEvent.slot.description && !(isMultiPlaceSlot(detailEvent.slot) && !detailEvent.booking) ? <p className="skywork-slot-detail-description">{detailEvent.slot.description}</p> : null}
              <div className="skywork-slot-detail-grid">
                <div>
                  <FaClock aria-hidden="true"/>
                  <span><small>{t('DURATION')}</small><strong>{moment.duration(moment(detailEvent.end).diff(moment(detailEvent.start))).asMinutes()} min</strong></span>
                </div>
                {detailEvent.slot.presetPlaces > 1 ? (
                  <div>
                    <FaUsers aria-hidden="true"/>
                    <span><small>{t('BOOKING_SLOT_AVAILABLE_PLACES')}</small><strong>{detailEvent.slot.availablePlaces}/{detailEvent.slot.presetPlaces}</strong></span>
                  </div>
                ) : null}
                {detailEvent.slot.minimumPlayers !== null ? (
                  <div>
                    <FaUserFriends aria-hidden="true"/>
                    <span><small>{t('BOOKING_SLOT_MINIMUM_PLAYERS')}</small><strong>{detailEvent.slot.minimumPlayers}</strong></span>
                  </div>
                ) : null}
                {detailEvent.slot.costsPrice > 0 ? (
                  <div>
                    <FaEuroSign aria-hidden="true"/>
                    <span><small>{t('BOOKING_SLOT_FIXED_COSTS')}</small><strong>{(detailEvent.slot.costsPrice / 100).toFixed(2)} €</strong></span>
                  </div>
                ) : null}
              </div>
              {detailEvent.slot.state === 'available' ? (
                <div className={`skywork-slot-detail-payment ${field(detailEvent.slot.fieldId).payment ? 'skywork-slot-detail-payment--online' : 'skywork-slot-detail-payment--club'}`}>
                  <FaCreditCard aria-hidden="true"/>
                  <span>
                    <strong>{field(detailEvent.slot.fieldId).payment ? t('ONLINE_PAYMENT') : t('SKYWORK_PAY_AT_CLUB')}</strong>
                    {!field(detailEvent.slot.fieldId).payment && detailEvent.slot.price > 0 ? <small>{discountedSlotPrice(detailEvent.slot).toFixed(2)} €</small> : null}
                  </span>
                </div>
              ) : null}
              {field(detailEvent.slot.fieldId).recording && detailEvent.slot.videoPurchasable ? (
                <div className="skywork-slot-detail-video"><FaPlayCircle aria-hidden="true"/><span>{detailEvent.slot.videoReady ? t('VIDEO_PURCHASED_AVAILABLE') : t('VIDEO_STORE')}</span></div>
              ) : null}
              {detailEvent.slot.openMatch ? (
                <div className="skywork-slot-open-match-panel">
                  <span><FaUsers aria-hidden="true"/> {t('OPEN_MATCH')}</span>
                  <strong>{detailEvent.slot.openMatch.playersJoined}/{detailEvent.slot.openMatch.playersRequired} giocatori</strong>
                  <small>Livello {detailEvent.slot.openMatch.minLevel || '-'} - {detailEvent.slot.openMatch.maxLevel || '-'} · quota a persona {detailEvent.slot.openMatch.credit && detailEvent.slot.openMatch.credit.requiredEur ? detailEvent.slot.openMatch.credit.requiredEur.toFixed(2) : '-'} €</small>
                  {detailEvent.slot.openMatch.currentUserJoined ? <em>{t('OPEN_MATCH_ALREADY_JOINED')}</em> : null}
                </div>
              ) : null}
              </Fragment>
              )}
            </Modal.Body>
            <Modal.Footer className="skywork-slot-detail-footer">
              <Button variant="outline-secondary" onClick={closeSlotDetail}>{t('CLOSE_BUTTON')}</Button>
              {detailBookingActive ? (
                <Fragment>
                  {canCreateOpenMatch(detailEvent) ? (
                    <Button type="button" variant="outline-primary" onClick={() => openOpenMatchModal(detailEvent)}>
                      <FaUsers aria-hidden="true"/> {t('OPEN_MATCH')}
                    </Button>
                  ) : null}
                  {detailEvent.slot.openMatch && detailEvent.slot.openMatch.status === 'open' && !detailEvent.slot.openMatch.currentUserJoined ? (
                    <Button type="button" variant="outline-primary" onClick={() => handleJoinOpenMatchFromSlot(detailEvent)}>
                      <FaUsers aria-hidden="true"/> Partecipa
                    </Button>
                  ) : null}
                  <Button variant="outline-primary" onClick={() => {
                    hideBookingSlotModal();
                    setDetailBookingActive(false);
                  }}>{t('BOOKING_SLOT_BACK_TO_DETAILS')}</Button>
                  {booking && booking.bookingId ? (
                    <Fragment>
                      <Button type="button" variant="secondary" onClick={handleDeleteBooking(bookingSlot, selectedPartner.phoneNumber)}>{t('BOOKING_DELETE')}</Button>
                      {field((bookingSlot || bookingSlots[0]).fieldId).recording ? <Button type="button" variant="success" onClick={() => {
                        const targetBookingSlot = bookingSlot || bookingSlots[0];
                        showVideoStore(selectedPartner.uid, targetBookingSlot.fieldId, targetBookingSlot.startDatetime, targetBookingSlot.endDatetime);
                      }}>{t('GO_TO_VIDEO_STORE')}</Button> : null}
                    </Fragment>
                  ) : null}
                </Fragment>
              ) : detailActionAvailable(detailEvent) ? (
                <Fragment>
                  {canCreateOpenMatch(detailEvent) ? (
                    <Button type="button" variant="outline-primary" onClick={() => openOpenMatchModal(detailEvent)}>
                      <FaUsers aria-hidden="true"/> {t('OPEN_MATCH')}
                    </Button>
                  ) : null}
                  {detailEvent.slot.openMatch && detailEvent.slot.openMatch.status === 'open' && !detailEvent.slot.openMatch.currentUserJoined ? (
                    <Button type="button" variant="outline-primary" onClick={() => handleJoinOpenMatchFromSlot(detailEvent)}>
                      <FaUsers aria-hidden="true"/> Partecipa alla partita
                    </Button>
                  ) : null}
                  <Button variant="primary" onClick={() => handleDetailAction(detailEvent)}>{mobileSlotAction(detailEvent)}</Button>
                </Fragment>
              ) : null}
            </Modal.Footer>
          </Fragment>
        ) : null}
      </Modal>
      {
        (bookingSlot === null && bookingSlots === null) || detailBookingActive ?
          null :
          <Modal
            show={isBookingSlotModalVisible}
            onHide={hideBookingSlotModal}
            dialogClassName={`skywork-slot-detail-dialog ${isMobile ? 'skywork-slot-detail-dialog--mobile' : 'skywork-slot-detail-dialog--desktop'}`}
            className="skywork-slot-detail-modal"
          >
            <Modal.Header closeButton className={`skywork-slot-detail-header skywork-slot-detail-header--${bookingSlotClass(bookingSlot || bookingSlots[0])}`}>
              <Modal.Title>{t('BOOKING')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="skywork-slot-detail-body skywork-slot-detail-body--booking">
              <PlayerBooking
                booking={booking}
                skyworkEmbedded
                save={saveBooking}
                partner={selectedPartner}
                slot={bookingSlot}
                slots={bookingSlots}
                discount={bookingSlot? discount(bookingSlot.fieldId) : discount(field(bookingSlots[0].fieldId))}
                field={bookingSlot ? field(bookingSlot.fieldId) : field(bookingSlots[0].fieldId)}
                dedicatedAffiliations={dedicatedAffiliations(bookingSlot ? bookingSlot.fieldId : bookingSlots[0].fieldId)}
                startBookingTopUp={startBookingTopUp}
              />
            </Modal.Body>
            <Modal.Footer className="skywork-slot-detail-footer">
              <Button variant="primary" onClick={hideBookingSlotModal}>
                {t('CLOSE_BUTTON')}
              </Button>
              {
                booking === null || !booking.bookingId ?
                  null :
                  <Fragment>
                    <Button type="button" variant="secondary" style={{marginLeft: 10}} onClick={handleDeleteBooking(bookingSlot, selectedPartner.phoneNumber)}>{t('BOOKING_DELETE')}</Button>
                    {field((bookingSlot || bookingSlots[0]).fieldId).recording ? <Button
                      type="button"
                      variant="success"
                      style={{marginLeft: 10}}
                      onClick={
                        () => {
                          const targetBookingSlot = bookingSlot ? bookingSlot : bookingSlots[0];
                          showVideoStore(selectedPartner.uid, targetBookingSlot.fieldId, targetBookingSlot.startDatetime, targetBookingSlot.endDatetime);
                        }
                      }
                    >
                      {t('GO_TO_VIDEO_STORE')}
                    </Button> : null}
                </Fragment>
              }
            </Modal.Footer>
          </Modal>
      }
      <Modal show={showOpenMatchModal} onHide={() => setShowOpenMatchModal(false)} centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('OPEN_MATCH_NEW')}</span>
            <Modal.Title>{detailEvent ? field(detailEvent.slot.fieldId).name : ''}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          {detailEvent ? (
            <div className="open-match-create-summary">
              <Badge variant="info">{field(detailEvent.slot.fieldId).sport}</Badge>
              <strong>{moment(detailEvent.start).format('DD/MM/YYYY HH:mm')} - {moment(detailEvent.end).format('HH:mm')}</strong>
              <small>{selectedPartner.name}</small>
            </div>
          ) : null}
          <Form>
            <Form.Group>
              <Form.Label>{t('OPEN_MATCH_TYPE')}</Form.Label>
              <div className="open-match-create-choice">
                <button type="button" className={openMatchDraft.matchType === 'friendly' ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, matchType: 'friendly'})}>{t('OPEN_MATCH_TYPE_FRIENDLY')}</button>
                <button type="button" className={openMatchDraft.matchType === 'competitive' ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, matchType: 'competitive'})}>{t('OPEN_MATCH_TYPE_COMPETITIVE')}</button>
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('OPEN_MATCH_REQUIRED_PLAYERS')}</Form.Label>
              <div className="open-match-create-choice">
                <button type="button" className={Number(openMatchDraft.playersRequired) === 2 ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, playersRequired: 2})}>{t('OPEN_MATCH_PLAYERS_COUNT_SIMPLE', {count: 2})}</button>
                <button type="button" className={Number(openMatchDraft.playersRequired) === 4 ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, playersRequired: 4})}>{t('OPEN_MATCH_PLAYERS_COUNT_SIMPLE', {count: 4})}</button>
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('LEVEL')}</Form.Label>
              <div className="open-match-create-choice">
                <button type="button" className={openMatchDraft.levelRangeMode === 'similar' ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, levelRangeMode: 'similar'})}>{t('OPEN_MATCH_LEVEL_SIMILAR')}</button>
                <button type="button" className={openMatchDraft.levelRangeMode === 'any' ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, levelRangeMode: 'any'})}>{t('OPEN_MATCH_LEVEL_ANY')}</button>
                <button type="button" className={openMatchDraft.levelRangeMode === 'custom' ? 'active' : ''} onClick={() => setOpenMatchDraft({...openMatchDraft, levelRangeMode: 'custom'})}>{t('CUSTOM')}</button>
              </div>
            </Form.Group>
            {openMatchDraft.levelRangeMode === 'custom' ? (
              <Form.Row>
                <Form.Group className="col-md-6">
                  <Form.Label>{t('OPEN_MATCH_MIN_LEVEL')}</Form.Label>
                  <Form.Control type="number" step="0.1" value={openMatchDraft.minLevel} onChange={event => setOpenMatchDraft({...openMatchDraft, minLevel: event.target.value})}/>
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>{t('OPEN_MATCH_MAX_LEVEL')}</Form.Label>
                  <Form.Control type="number" step="0.1" value={openMatchDraft.maxLevel} onChange={event => setOpenMatchDraft({...openMatchDraft, maxLevel: event.target.value})}/>
                </Form.Group>
              </Form.Row>
            ) : null}
            <Form.Group>
              <Form.Label>{t('TITLE')}</Form.Label>
              <Form.Control type="text" maxLength={120} value={openMatchDraft.title} onChange={event => setOpenMatchDraft({...openMatchDraft, title: event.target.value})} placeholder={t('OPEN_MATCH_TITLE_PLACEHOLDER')}/>
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('NOTES')}</Form.Label>
              <Form.Control as="textarea" rows={3} maxLength={500} value={openMatchDraft.notes} onChange={event => setOpenMatchDraft({...openMatchDraft, notes: event.target.value})} placeholder={t('OPEN_MATCH_NOTES_PLACEHOLDER')}/>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setShowOpenMatchModal(false)}>{t('CANCEL')}</Button>
          <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={openMatchLoading} onClick={saveOpenMatch}>
            <FaSave aria-hidden="true"/>{t(openMatchLoading ? 'OPEN_MATCH_CREATING' : 'CREATE_MATCH')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={openMatchRankingSlot !== null} onHide={() => setOpenMatchRankingSlot(null)} size="lg" centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('OPEN_MATCH_RANKING_REQUIRED')}</span>
            <Modal.Title>
              {t('OPEN_MATCH_CONFIGURE_SPORT', {sport: openMatchSportDisplayName(openMatchRankingSport)})}
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          <p className="open-match-ranking-intro">
            {t('OPEN_MATCH_RANKING_REQUIRED_BODY')}
          </p>
          <div className="open-match-ranking-sport-switch">
            {Object.entries(openMatchSportLabels).map(([sport, label]) => (
              <button
                key={sport}
                type="button"
                className={openMatchRankingSport === sport ? 'active' : ''}
                onClick={() => {
                  setOpenMatchRankingSport(sport);
                  setOpenMatchRankingAnswers({});
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {openMatchRankingSlot ? ((openMatchQuestionnaires[openMatchRankingSport] || {questions: [], options: []}).questions.map(question => (
            <div key={question.key} className="player-ranking-question">
              <strong>{rankingQuestionLabel(question, t)}</strong>
              <div className="player-ranking-options">
                {(question.options || (openMatchQuestionnaires[openMatchRankingSport] || {options: []}).options).map(option => (
                  <button
                    key={`${question.key}-${option.value}`}
                    type="button"
                    className={openMatchRankingAnswers[question.key] === option.value ? 'active' : ''}
                    onClick={() => setOpenMatchRankingAnswers({...openMatchRankingAnswers, [question.key]: option.value})}
                  >
                    {rankingOptionLabel(option, t)}
                  </button>
                ))}
              </div>
            </div>
          ))) : null}
        </Modal.Body>
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setOpenMatchRankingSlot(null)}>{t('CANCEL')}</Button>
          <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={openMatchRankingSaving} onClick={saveOpenMatchRankingAndRetry}>
            <FaSave aria-hidden="true"/>{openMatchRankingSaving ? t('SAVING') : t('SAVE_SELF_ASSESSMENT_SPORT', {sport: openMatchSportDisplayName(openMatchRankingSport)})}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={insufficientCredit !== null}
        onHide={() => setInsufficientCredit(null)}
        centered
        dialogClassName="skywork-invite-modal skywork-booking-credit-modal"
      >
        <Modal.Header closeButton>
          <div className="skywork-booking-credit-heading">
            <span><FaWallet aria-hidden="true"/></span>
            <div>
              <small>{t('BOOKING_CREDIT_MODAL_EYEBROW')}</small>
              <Modal.Title>{t('BOOKING_CREDIT_MODAL_TITLE')}</Modal.Title>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {insufficientCredit && insufficientCredit.event ? (
            <div className="skywork-invite-slot-summary">
              <strong>{field(insufficientCredit.event.slot.fieldId).name} · {sportLabelValue(field(insufficientCredit.event.slot.fieldId).sport) || t('SPORT')}</strong>
              <span>{moment(insufficientCredit.event.start).locale(i18n.language).format('dddd D MMMM')} · {moment(insufficientCredit.event.start).format('HH:mm')}–{moment(insufficientCredit.event.end).format('HH:mm')}</span>
            </div>
          ) : null}
          <p className="skywork-booking-credit-copy">{t('BOOKING_CREDIT_MODAL_BODY')}</p>
          {insufficientCredit ? (
            <div className="skywork-booking-credit-summary">
              <span><small>{t('BOOKING_CREDIT_REQUIRED')}</small><strong>€ {insufficientCredit.requiredCredit.toFixed(2)}</strong></span>
              <span><small>{t('BOOKING_CREDIT_AVAILABLE')}</small><strong>€ {insufficientCredit.wallet.toFixed(2)}</strong></span>
              <span><small>{t('BOOKING_CREDIT_TOPUP')}</small><strong>€ {insufficientCredit.topUpAmount.toFixed(2)}</strong></span>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="skywork-invite-footer skywork-booking-credit-actions">
          <Button type="button" variant="outline-primary" onClick={() => setInsufficientCredit(null)}>{t('CLOSE_BUTTON')}</Button>
          <Button
            type="button"
            className="skywork-primary-action"
            onClick={() => {
              if (!insufficientCredit || !insufficientCredit.event) return;
              const {topUpAmount, event} = insufficientCredit;
              setInsufficientCredit(null);
              startBookingTopUp(topUpAmount, event.slot.slotId, null, {
                partnerId: selectedPartner.uid,
                resourceId: event.slot.fieldId,
                resourceType: 'field',
                startDatetime: event.slot.startDatetime
              });
            }}
          ><FaWallet aria-hidden="true"/> {t('BOOKING_CREDIT_TOPUP_ACTION')}</Button>
        </Modal.Footer>
      </Modal>
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
      <SkyworkPastSlotModal show={showPastSlotModal} onHide={() => setShowPastSlotModal(false)}/>
      <BookingGuideModal show={showBookingGuide} onHide={() => setShowBookingGuide(false)}/>
    </Fragment>
  );
};

PlayerBookingCalendar.propTypes = {
  addToBookingQueue: PropTypes.func.isRequired,
  isLogged: PropTypes.bool,
  selectedPartner: PropTypes.object,
  selectedPartnerFields: PropTypes.array,
  slots: PropTypes.array,
  isBookingSlotModalVisible: PropTypes.bool,
  bookingSlot: PropTypes.object,
  hideBookingSlotModal: PropTypes.func.isRequired,
  bookingSlotSelected: PropTypes.func.isRequired,
  saveBooking: PropTypes.func.isRequired,
  bookingFieldId: PropTypes.number,
  bookings: PropTypes.array,
  booking: PropTypes.object,
  requestDelete: PropTypes.func.isRequired,
  purchaseVideo: PropTypes.func.isRequired,
  showMessage: PropTypes.func.isRequired,
  bookingMultiSlots: PropTypes.func.isRequired,
  bookingSlots: PropTypes.array,
  profile: PropTypes.object,
  token: PropTypes.string,
  acceptPrivacy: PropTypes.func.isRequired,
  getSlotsByWeek: PropTypes.func.isRequired,
  getSlotsByDay: PropTypes.func.isRequired,
  showVideoStore: PropTypes.func.isRequired,
  showCalendar: PropTypes.func.isRequired,
  filterBookableFields: PropTypes.bool.isRequired,
  affiliations: PropTypes.array,
  affiliationsStatus: PropTypes.string,
  refreshPlayerAffiliations: PropTypes.func,
  deleteBooking: PropTypes.func.isRequired,
  enforceWalletBeforeBooking: PropTypes.bool,
  goToProfile: PropTypes.func.isRequired,
  startBookingTopUp: PropTypes.func.isRequired,
  startGuestTopUp: PropTypes.func.isRequired,
  resumeSlotId: PropTypes.string,
  onResumeSlotHandled: PropTypes.func,
  restoreBookingTopupContext: PropTypes.func
};

PlayerBookingCalendar.defaultProps = {
  isLogged: false,
  selectedPartner: null,
  selectedPartnerFields: [],
  slots: [],
  isBookingSlotModalVisible: false,
  bookingSlot: null,
  bookingFieldId: null,
  bookings: [],
  booking: null,
  bookingSlots: null,
  profile: null,
  token: null,
  affiliations: [],
  affiliationsStatus: 'idle',
  refreshPlayerAffiliations: () => Promise.resolve(false),
  enforceWalletBeforeBooking: false,
  resumeSlotId: null,
  onResumeSlotHandled: () => {},
  restoreBookingTopupContext: () => Promise.resolve(false)
};

export default PlayerBookingCalendar;
